import type { StatusTone } from '@noor/ui';
import { systemUserRoleSchema, systemUserStatusSchema } from '../model/schema';
import type { SystemUser } from '../model/schema';

/**
 * Display fallbacks (merchant `orDash` parity — duplicated, slices stay
 * isolated): dash for missing OR empty strings.
 */
export function orDash(value: string | undefined | null): string {
  if (value === undefined || value === null || value === '') return '—';
  return value;
}

/** E.164 with a leading `+` — the API rejects anything else. */
export function toE164(phone: string | undefined | null): string {
  const digits = (phone ?? '').replace(/\D/g, '');
  return digits ? `+${digits}` : '';
}

/** `DD.MM.YYYY HH:mm` (merchant `formatDateTime` parity — see `orDash` above). */
export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(at.getDate())}.${pad(at.getMonth() + 1)}.${at.getFullYear()} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/** Full name, falling back to the phone when auth never stored a name. */
export function getSystemUserDisplayName(user: SystemUser): string {
  const first = user.user?.first_name?.trim() ?? '';
  const last = user.user?.last_name?.trim() ?? '';
  const full = `${first} ${last}`.trim();
  return full === '' ? user.phone : full;
}

/** Whether the identity currently holds the banned status. */
export function isSystemUserBanned(user: SystemUser): boolean {
  return user.status === 'banned';
}

/**
 * i18n key for a role — call sites render `t(roleLabelKeyFor(role))`. A role
 * newer than this build renders raw (i18next echoes unknown keys back).
 */
export function roleLabelKeyFor(role: string): string {
  return systemUserRoleSchema.safeParse(role).success ? `users:roles.${role}` : role;
}

/**
 * i18n key for a status — call sites render `t(statusLabelKeyFor(status))`.
 * Unknown statuses render raw, like roles above.
 */
export function statusLabelKeyFor(status: string): string {
  return systemUserStatusSchema.safeParse(status).success
    ? `users:statuses.${status}`
    : status;
}

/** Status pill tone for the `StatusBadge` component. */
export function systemUserStatusTone(status: string): StatusTone {
  switch (status) {
    case 'active':
      return 'active';
    case 'pending':
      return 'pending';
    case 'banned':
    case 'deleted':
    case 'rejected':
      return 'danger';
    case 'suspended':
    default:
      return 'neutral';
  }
}
